
import { Component, Vue, Watch } from 'vue-property-decorator';
import {
  OverallStatisticDisplayInterface,
  OverallTotalStatisticInterface,
  OverallUserStatisticInterface,
  StatisticFilterData,
  StatisticPeriodInterface,
  StatisticRepositoryInterface,
  TotalStatisticInterface,
  UserStatisticDisplayInterface,
  UserStatisticInterface,
} from '@/services/QC-V2/statistic/interfaces';
import { DataTableHeader } from 'vuetify';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import { UserRepoInterface } from '@/services/Users/Interfaces';

interface DataTableOptions {
  page: number,
  itemsPerPage: number,
  sortBy: string[],
  sortDesc: boolean[],
  groupBy: string[],
  groupDesc: boolean[],
  multiSort: boolean,
  mustSort: boolean
}

interface FilterItemInterface {
  text: string,
  value: string | boolean | undefined,
}

interface FilterInterface {
  text: string,
  value: string,
  icon: string,
  items: FilterItemInterface[],
  selected: FilterItemInterface,
}

interface UserSelectInterface {
  id: string;
  email: string;
}

@Component({
  inject: [
    'statisticRepositoryV2',
    'profileRepository',
    'userRepository',
  ],
})
export default class Statistics extends Vue {
  private statisticRepositoryV2!: StatisticRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private userRepository!: UserRepoInterface;

  private totalItems: number = 0;
  private overallStatistic: OverallTotalStatisticInterface = {
    chats: 0,
    tickets: 0,
    chatFeedbacks: 0,
    chatEscalations: 0,
    ticketFeedbacks: 0,
    ticketEscalations: 0,
  };

  private usersForSelect: UserSelectInterface[] = [];

  private profilesMap: ProfileMapInterface = {};
  private usersStatistic: OverallUserStatisticInterface[] = [];
  private headers: DataTableHeader[] = [
    { text: 'User', value: 'userId', sortable: false },
    { text: 'Chat Reviews', value: 'chats' },
    { text: 'Ticket Reviews', value: 'tickets' },
    { text: 'Chat Feedbacks', value: 'chatFeedbacks' },
    { text: 'Ticket Feedbacks', value: 'ticketFeedbacks' },
    { text: 'Chat Escalations', value: 'chatEscalations' },
    { text: 'Ticket Escalations', value: 'ticketEscalations' },
  ];

  private filterData: StatisticFilterData = {
    startDate: '',
    endDate: '',
    userId: '',
    page: 1,
    sortBy: 'chats',
    order: 'DESC',
  };

  private options: DataTableOptions = {
    itemsPerPage: 10,
    page: this.filterData.page,
    sortBy: ['chats'],
    sortDesc: [true],
  } as DataTableOptions;

  private filter: FilterInterface = {
    text: 'Date',
    value: 'startDate',
    icon: 'mdi-calendar',
    items: [
      { text: 'TODAY', value: 'today' },
      { text: 'YESTERDAY', value: 'yesterday' },
      { text: 'LAST 7 DAYS', value: '7 days' },
      { text: 'LAST 30 DAYS', value: '30 days' },
      { text: 'LAST MONTH', value: 'last month' },
      { text: 'CURRENT MONTH', value: 'current month' },
      { text: 'CUSTOM PERIOD', value: 'custom period' },
    ],
    selected: { text: 'LAST 7 DAYS', value: '7 days' },
  };
  private customDates: string[] = [];
  private period: StatisticPeriodInterface | undefined = undefined;
  private minDate!: string;

  private loading: boolean = true;

  private async created(): Promise<void> {
    await this.applyFilter();
    this.loading = false;
  }

  private async initialize(): Promise<void> {
    try {
      this.resetData();
      this.loading = true;
      const totalStatistic = await this.statisticRepositoryV2.getOverallTotalStatistic(this.filterData);
      const [userStatistic, totalItems] = await this.statisticRepositoryV2.getOverallUserStatistic(this.filterData);
      await this.fetchUsersForSelect();
      await this.fetchUsersData(userStatistic);
      this.setMinDate();

      this.overallStatistic = totalStatistic;
      this.usersStatistic = userStatistic;
      this.totalItems = totalItems;

      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private resetData(): void {
    this.usersStatistic = [];
    this.overallStatistic = {
      chats: 0,
      tickets: 0,
      chatFeedbacks: 0,
      chatEscalations: 0,
      ticketFeedbacks: 0,
      ticketEscalations: 0,
    };
  }

  @Watch('options', { deep: true })
  private async handlePageChange(): Promise<void> {

    const sortChange = this.options.sortBy[0]
      && (this.options.sortBy[0] !== this.filterData.sortBy
        || (this.options.sortDesc[0] ? 'DESC' : 'ASC') !== this.filterData.order);

    if (sortChange) {
      await this.changeSort();
    }

    if (this.filterData.page === this.options.page) {
      return;
    }

    this.filterData.page = this.options.page;
    const [userStatistic, totalItems] = await this.statisticRepositoryV2.getOverallUserStatistic(this.filterData);

    await this.fetchUsersData(userStatistic);
    this.usersStatistic = [];
    this.usersStatistic = userStatistic;
    this.totalItems = totalItems;
  }

  private async changeSort(): Promise<void> {
    this.filterData.page = 1;
    this.options.page = 1;
    this.filterData.sortBy = this.options.sortBy[0];
    this.filterData.order = this.options.sortDesc[0] ? 'DESC' : 'ASC';
    const [userStatistic, totalItems] = await this.statisticRepositoryV2.getOverallUserStatistic(this.filterData);

    await this.fetchUsersData(userStatistic);
    this.usersStatistic = [];
    this.usersStatistic = userStatistic;
    this.totalItems = totalItems;
  }

  private async fetchUsersForSelect(): Promise<void> {
    const users = await this.userRepository.getQaUsers();
    this.usersForSelect = users.map((user) => ({ id: user.id, email: user.email }));
  }

  private async fetchUsersData(userStatistic: OverallUserStatisticInterface[]): Promise<void> {
    const ids = userStatistic.map((statistic) => statistic.userId);
    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

  private async redirectToUserStatistics(statistic: UserStatisticDisplayInterface): Promise<void> {
    await this.$router.push({ name: 'user-statistics', params: { userId: statistic.userId } });
  }

  private selectUser(): void {
    this.period = undefined;
    this.filterData.startDate = undefined;
    this.filterData.endDate = undefined;
    this.filterData.page = 1;
    this.options.page = 1;
    this.initialize();
  }

  @Watch('filter.selected')
  private async datesFilter(): Promise<void> {
    const sameDateFilter = this.customDates.includes(String(this.period?.startDate)) && this.customDates.includes(String(this.period?.endDate));
    if (this.filter?.selected?.value !== 'custom period' || this.customDates.length < 2 || sameDateFilter) {
      return;
    }

    await this.applyFilter();
  }

  private async applyFilter(): Promise<void> {
    this.filterData.userId = '';
    this.filterData.page = 1;
    this.options.page = 1;
    this.period = this.getDateFilterData();
    if (this.period) {
      this.filterData.startDate = this.period.startDate;
      this.filterData.endDate = this.period.endDate;
    }
    await this.initialize();
  }

  private getDateFilterData(): { startDate: string, endDate: string } | undefined {
    if (!this.filter.selected || !this.filter.selected.value) {
      return undefined;
    }

    const selected = this.filter.selected.value as string;

    let startDate: Date = new Date();
    let endDate: Date = new Date();
    let filterApplied = false;

    if (selected === 'today') {
      this.customDates = [];
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();

      filterApplied = true;
    }

    if (selected === 'yesterday') {
      this.customDates = [];
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
      endDate.setHours(23, 59, 59, 59);

      filterApplied = true;
    }

    if (selected.endsWith('days')) {
      this.customDates = [];
      const [count] = selected.split(' ');
      startDate = new Date();
      startDate.setDate(startDate.getDate() - Number(count));
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'last month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);

      endDate = new Date(date.getFullYear(), date.getMonth(), 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'current month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth(), 1);

      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }


    if (selected === 'custom period') {
      if (this.customDates.length < 2) {
        return undefined;
      }

      const [startDateStr, endDateStr] = this.customDates;
      startDate = new Date(startDateStr);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(endDateStr);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (!filterApplied) {
      return undefined;
    }

    if (startDate > endDate) {
      const temp = startDate;
      startDate = endDate;
      endDate = temp;
    }

    return { startDate: this.convertDateToString(startDate), endDate: this.convertDateToString(endDate) };
  }

  private convertDateToString(date: Date): string {
    return date.toLocaleString('sv-se').substr(0, 10);
  }

  private setMinDate(): void {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    this.minDate = date.toISOString();
  }

  private get datesRangeText(): string {
    return this.customDates.join(' ~ ');
  }

  private preventClose(event: PointerEvent, item: FilterItemInterface): void {
    if (item.value === 'custom period') {
      this.filter.selected = item;
      event.stopPropagation();
      event.preventDefault();
      this.keepCustomPeriodActive(this.filter);
    }
  }

  private keepCustomPeriodActive(filter: FilterInterface): void {
    setTimeout(() => {
      if (filter.selected && filter.selected.value === 'custom period') {
        filter.selected = { text: 'CUSTOM PERIOD', value: 'custom period' };
      }
    }, 10);
  }
}
